// The browser half of CloudFront signed-URL access: fetch a short-lived token,
// attach it to every request under one protected prefix, and re-fetch on a 403
// so an expiry mid-session self-heals instead of leaving the map empty.
//
// This is _weatherCdn.js's machinery, lifted out unchanged so a second prefix
// can use it without a second copy of logic whose failure modes are all
// timing-shaped — a stampede on the token endpoint, a 403 storm from missing
// objects, a token replaced by a sibling request mid-flight. One implementation,
// one set of tests. `createCdnFetch` returns a fetch-alike bound to one prefix;
// _weatherCdn.js and _marineCdn.js are its two instances.
//
// SIGN AT FETCH TIME, NEVER IN A CACHE KEY. Every caller keeps passing the
// plain, unsigned URL around; the token is attached inside the wrapper and
// nowhere else. That is load-bearing, not tidiness:
//   - radarGL caches decoded textures in a Map keyed by frame URL, pmtiles
//     memoizes each opened archive by its source key, and MapLibre keys tiles by
//     URL. A rotating token baked into any of those blows the cache away on
//     every refresh and re-downloads everything.
//   - a pmtiles reader caches an archive failure aggressively (an immutable
//     object that 403s will never appear), so retrying the 403 in here, under a
//     fresh token, is what keeps a routine expiry from killing a layer for the
//     rest of the session.
// It is also the shape native will want: a constant URL keeps on-disk tile cache
// keys stable, which is why native gets the cookie/header form rather than
// params.
//
// Nothing here runs at import time — these modules are eager-imported during SSR
// (see entrypoints/resolvePage.jsx) and must not touch window, location or fetch
// until a client effect actually calls in.

const CDN_HOST = 'd1nezfegjr0s5p.cloudfront.net'

// Treat a token as spent this long before it actually expires, so a request that
// starts just under the wire doesn't land just over it.
const EXPIRY_SKEW_MS = 60_000

// After the token endpoint fails (offline, 429, 503-not-configured), stop asking
// for a while. Requests continue unsigned in the meantime, which is exactly
// right before enforcement is enabled and fails visibly at the CDN after.
const FAILURE_QUIET_MS = 60_000

// A 403 is ambiguous: an expired token, or an object that genuinely isn't there
// (S3 reports a missing key as 403, and radar playback walks over plenty of
// those). Refreshing on every one would mean a token round trip per missing
// object, so a 403-FORCED refresh happens at most this often. This throttles
// only forced refreshes, never the first fetch or an ordinary expiry renewal —
// those have to be free to happen the moment they are needed.
const REFRESH_THROTTLE_MS = 30_000

// One protected prefix: its token endpoint, its module state, and the fetch
// wrapper and helpers that read them. State is per-instance, so a weather 403
// storm cannot put the marine token into its quiet period.
export function createCdnFetch({ endpoint, protectedPath, host = CDN_HOST }) {
  let current = null // { params, expiresAt, gen }
  let inFlight = null
  let gen = 0
  let quietUntil = 0
  let lastForcedRefreshAt = 0

  function isProtected(url) {
    try {
      const u = new URL(url, typeof location === 'undefined' ? undefined : location.href)
      return u.host === host && u.pathname.startsWith(protectedPath)
    } catch {
      return false
    }
  }

  function usable(token) {
    return !!token && Date.now() < token.expiresAt - EXPIRY_SKEW_MS
  }

  // Append the token to a URL's query string, ahead of any fragment.
  function withParams(url, token) {
    if (!token) return url
    const hash = url.indexOf('#')
    const base = hash === -1 ? url : url.slice(0, hash)
    const tail = hash === -1 ? '' : url.slice(hash)
    return `${base}${base.includes('?') ? '&' : '?'}${token.params}${tail}`
  }

  // One request at a time, shared by every concurrent caller. Resolves to the
  // token, or to null when signing is unavailable — never rejects, because a
  // missing token must degrade to an unsigned request rather than break a fetch.
  // `force` skips the failure quiet period, and only a signature rejection sets
  // it. The quiet period is right for the ordinary path — an endpoint that just
  // failed should not be asked again on every tile — but it is wrong the moment
  // the CDN actually refuses us, because that refusal is evidence the answer
  // matters NOW. Without this, one failed token fetch left a client unable to
  // recover for a full minute; with the prefixes enforced that is a minute of
  // 403s, and any archive whose header read falls inside it is dead for the
  // session (pmtiles caches the rejected header promise and never evicts it).
  // The 30s forced-refresh throttle in refreshToken is what still bounds this.
  function fetchToken(force = false) {
    if (inFlight) return inFlight
    if (!force && Date.now() < quietUntil) return Promise.resolve(null)

    inFlight = fetch(endpoint, { headers: { Accept: 'application/json' } })
      .then(async (resp) => {
        // 503 is the documented "not configured yet" answer, and 429 is the
        // rate limiter. Both mean "carry on without a token", same as a network
        // error.
        if (!resp.ok) throw new Error(`${endpoint} ${resp.status}`)
        const body = await resp.json()
        if (!body || typeof body.params !== 'string' || body.params === '')
          throw new Error(`${endpoint}: malformed body`)
        const expiresAt = Date.parse(body.expires_at)
        gen += 1
        current = {
          params: body.params,
          // An unparseable expiry shouldn't discard a working token; assume the
          // short end of the server's window and let the 403 path correct us.
          expiresAt: Number.isNaN(expiresAt) ? Date.now() + 10 * 60_000 : expiresAt,
          gen,
        }
        return current
      })
      .catch(() => {
        quietUntil = Date.now() + FAILURE_QUIET_MS
        return null
      })
      .finally(() => {
        inFlight = null
      })

    return inFlight
  }

  function tokenNow() {
    return usable(current) ? Promise.resolve(current) : fetchToken()
  }

  // Replace the token that produced a 403. `stale` is the token the failed
  // request actually used — if it has already been superseded (a sibling
  // request beat us to it) take the newer one rather than stampeding the
  // endpoint.
  function refreshToken(stale) {
    if (current && (!stale || current.gen !== stale.gen)) return Promise.resolve(current)
    if (inFlight) return inFlight
    if (lastForcedRefreshAt && Date.now() - lastForcedRefreshAt < REFRESH_THROTTLE_MS)
      return Promise.resolve(null)

    lastForcedRefreshAt = Date.now()
    return fetchToken(true)
  }

  // Drop-in replacement for fetch() against the protected prefix. Same
  // signature, same Response — including a genuine 403, which callers still
  // need to see.
  async function cdnFetch(url, init) {
    if (!isProtected(url)) return fetch(url, init)

    const token = await tokenNow()

    let resp
    try {
      resp = await fetch(withParams(url, token), init)
    } catch (err) {
      // A rejected fetch is usually a real network failure — but not always.
      // When CloudFront rejects a signature it generates the 403 ITSELF, and a
      // CloudFront-generated error only carries CORS headers if the behaviour
      // has a response-headers policy attached. Without one, a cross-origin
      // caller never sees status 403: the browser blocks the response and fetch
      // rejects with an opaque TypeError. Attaching that policy is a rollout
      // step, and this is the belt to its braces — otherwise a misconfigured
      // behaviour turns every expiry into a dead layer rather than one retry.
      //
      // Holding no token is a case to retry too, not a reason to give up: a
      // client whose token fetch failed once is exactly the client whose next
      // request gets refused, and now that both prefixes are enforced an
      // unsigned request is a failure rather than a harmless one. The forced
      // refresh is throttled to once per 30s and returns null when spent, so a
      // genuine network outage still ends in the original error rather than a
      // loop.
      if (err?.name === 'AbortError') throw err
      const renewed = await refreshToken(token)
      if (!renewed || (token && renewed.gen === token.gen)) throw err
      return fetch(withParams(url, renewed), init)
    }

    if (resp.status !== 403) return resp

    const fresh = await refreshToken(token)
    // No newer token to try — hand back the 403 we got. For a missing object
    // that IS the right answer, and callers already treat it as "not
    // published".
    if (!fresh || (token && fresh.gen === token.gen)) return resp

    return fetch(withParams(url, fresh), init)
  }

  return {
    fetch: cdnFetch,
    isProtected,
    withParams,
    // For a caller that owns its own request path and cannot go through
    // cdnFetch — the PMTiles source, whose byte-range reads are issued by the
    // library. It gets the same token and the same forced-refresh throttle
    // rather than a second, subtly different implementation.
    tokenNow,
    refreshToken,
    reset() {
      current = null
      inFlight = null
      gen = 0
      quietUntil = 0
      lastForcedRefreshAt = 0
    },
  }
}
